// Shared period-comparison primitives: the compare selector (this week vs
// last, this month vs last, or two arbitrary ranges), delta rendering with
// metric-aware color, and the "no data" note. Deltas only ever come from two
// real stored ranges — when a side has no underlying records the pages say so
// instead of showing a zero.
import { useEffect, useState } from 'react';
import { ArrowDownRight, ArrowUpRight, ChevronDown, ChevronRight, Info } from 'lucide-react';
import clsx from 'clsx';

export interface DateRange { start: string; end: string } // YYYY-MM-DD, inclusive
export interface ComparePair { a: DateRange; b: DateRange } // a = current, b = prior
export type CompareMode = 'off' | 'week' | 'month' | 'custom';

const iso = (d: Date) =>
  `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
const addDays = (d: Date, n: number) => { const x = new Date(d); x.setDate(x.getDate() + n); return x; };
const parseDay = (s: string) => new Date(`${s}T00:00:00`);

export function rangeLabel(r: DateRange): string {
  const s = parseDay(r.start);
  const e = parseDay(r.end);
  const needYear = s.getFullYear() !== new Date().getFullYear() || e.getFullYear() !== new Date().getFullYear();
  const day = (d: Date) =>
    d.toLocaleDateString('en-US', needYear ? { month: 'short', day: 'numeric', year: 'numeric' } : { month: 'short', day: 'numeric' });
  if (r.start === r.end) return day(s);
  if (s.getMonth() === e.getMonth() && s.getFullYear() === e.getFullYear()) {
    const base = `${s.toLocaleDateString('en-US', { month: 'short' })} ${s.getDate()}–${e.getDate()}`;
    return needYear ? `${base}, ${e.getFullYear()}` : base;
  }
  return `${day(s)} – ${day(e)}`;
}

// Both pairs compare the same number of elapsed days, so mid-period deltas
// stay like-for-like instead of pitting a partial week against a full one.
export function weekPair(): ComparePair {
  const now = new Date();
  const dow = (now.getDay() + 6) % 7; // 0 = Monday, matching the P&L week
  const aStart = addDays(now, -dow);
  return {
    a: { start: iso(aStart), end: iso(now) },
    b: { start: iso(addDays(aStart, -7)), end: iso(addDays(now, -7)) },
  };
}

export function monthPair(): ComparePair {
  const now = new Date();
  const aStart = new Date(now.getFullYear(), now.getMonth(), 1);
  const prevMonthDays = new Date(now.getFullYear(), now.getMonth(), 0).getDate();
  const bStart = new Date(now.getFullYear(), now.getMonth() - 1, 1);
  const bEnd = new Date(now.getFullYear(), now.getMonth() - 1, Math.min(now.getDate(), prevMonthDays));
  return {
    a: { start: iso(aStart), end: iso(now) },
    b: { start: iso(bStart), end: iso(bEnd) },
  };
}

// The account's stores, as cached at login (same source the P&L page uses).
export function storedRestaurants(): Array<{ id: string; name: string }> {
  try { return JSON.parse(localStorage.getItem('manager_restaurants') || '[]'); } catch { return []; }
}

export const moneyCents = (v: number | null | undefined) =>
  v === null || v === undefined
    ? '—'
    : `${v < 0 ? '-' : ''}$${(Math.abs(v) / 100).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

const MODE_LABELS: Array<[CompareMode, string]> = [
  ['off', 'Off'],
  ['week', 'Week vs last'],
  ['month', 'Month vs last'],
  ['custom', 'Two periods'],
];

export function ComparePeriodBar({ onChange }: { onChange: (pair: ComparePair | null) => void }) {
  const [mode, setMode] = useState<CompareMode>('off');
  const [custom, setCustom] = useState<ComparePair>(() => monthPair());

  const pair: ComparePair | null =
    mode === 'off' ? null : mode === 'week' ? weekPair() : mode === 'month' ? monthPair() : custom;

  useEffect(() => {
    onChange(pair);
    // Deliberately keyed to the selection, not the callback identity — pages
    // pass inline setters and must not retrigger this on every render.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mode, custom]);

  const setCustomPart = (side: 'a' | 'b', field: 'start' | 'end', value: string) =>
    setCustom((c) => ({ ...c, [side]: { ...c[side], [field]: value } }));

  return (
    <div className="card p-4 space-y-3">
      <div className="flex flex-wrap items-center gap-3">
        <span className="text-sm font-medium text-slate-600">Compare</span>
        <div className="flex flex-wrap gap-1">
          {MODE_LABELS.map(([m, label]) => (
            <button
              key={m}
              onClick={() => setMode(m)}
              className={clsx(
                'px-3 py-1.5 rounded-lg text-sm font-medium transition-colors',
                mode === m ? 'bg-primary-600 text-white' : 'bg-slate-100 text-slate-700 hover:bg-slate-200'
              )}
            >
              {label}
            </button>
          ))}
        </div>
        {pair && (
          <span className="text-sm text-slate-500">
            {rangeLabel(pair.a)} <span className="text-slate-400">vs</span> {rangeLabel(pair.b)}
          </span>
        )}
      </div>
      {mode === 'custom' && (
        <div className="flex flex-wrap items-center gap-2 text-sm">
          <span className="text-xs font-medium text-slate-500">Period A</span>
          <input type="date" value={custom.a.start} onChange={(e) => setCustomPart('a', 'start', e.target.value)} className="input w-auto" />
          <span className="text-slate-400">to</span>
          <input type="date" value={custom.a.end} onChange={(e) => setCustomPart('a', 'end', e.target.value)} className="input w-auto" />
          <span className="text-xs font-medium text-slate-500 ml-2">Period B</span>
          <input type="date" value={custom.b.start} onChange={(e) => setCustomPart('b', 'start', e.target.value)} className="input w-auto" />
          <span className="text-slate-400">to</span>
          <input type="date" value={custom.b.end} onChange={(e) => setCustomPart('b', 'end', e.target.value)} className="input w-auto" />
        </div>
      )}
      {(mode === 'week' || mode === 'month') && (
        <p className="text-xs text-slate-400">
          Both sides cover the same elapsed days of the {mode}, so the numbers compare like for like.
        </p>
      )}
    </div>
  );
}

// A delta between two real values. kind 'cents' renders "+$412 · +6.3%";
// kind 'points' renders "+1.2 pts" (percent-of-a-percent reads as noise).
// goodWhenUp drives the color: profit up = green, cost up = red.
export function Delta({ now, prev, kind, goodWhenUp }: {
  now: number | null | undefined;
  prev: number | null | undefined;
  kind: 'cents' | 'points';
  goodWhenUp: boolean;
}) {
  if (now === null || now === undefined || prev === null || prev === undefined) {
    return <span className="text-sm text-slate-400">—</span>;
  }
  const diff = now - prev;
  if (diff === 0) return <span className="text-sm text-slate-400">no change</span>;
  const up = diff > 0;
  const sign = up ? '+' : '-';
  const abs = kind === 'cents'
    ? `${sign}$${(Math.abs(diff) / 100).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`
    : `${sign}${Math.abs(diff).toFixed(1)} pts`;
  const pctPart = kind === 'cents' && prev !== 0
    ? ` · ${sign}${Math.abs((diff / Math.abs(prev)) * 100).toFixed(1)}%`
    : '';
  const Icon = up ? ArrowUpRight : ArrowDownRight;
  return (
    <span
      className={clsx(
        'inline-flex items-center gap-0.5 text-sm font-medium tabular-nums whitespace-nowrap',
        up === goodWhenUp ? 'text-success-600' : 'text-danger-600'
      )}
    >
      {abs}{pctPart}
      <Icon className="w-3.5 h-3.5" />
    </span>
  );
}

export function NoData({ range }: { range?: DateRange }) {
  return (
    <span className="text-sm text-slate-500">
      {range ? `No data for ${rangeLabel(range)}` : 'No data for this period'}
    </span>
  );
}

// One metric compared across the two periods, as a table row.
export function CompareRow({ label, a, b, kind, goodWhenUp }: {
  label: string;
  a: number | null | undefined;
  b: number | null | undefined;
  kind: 'cents' | 'points';
  goodWhenUp: boolean;
}) {
  const fmt = (v: number | null | undefined) =>
    v === null || v === undefined ? '—' : kind === 'cents' ? moneyCents(v) : `${v.toFixed(1)}%`;
  return (
    <tr>
      <td className="table-cell font-medium text-slate-700">{label}</td>
      <td className="table-cell text-right tabular-nums">{fmt(a)}</td>
      <td className="table-cell text-right tabular-nums text-slate-500">{fmt(b)}</td>
      <td className="table-cell text-right">
        <Delta now={a} prev={b} kind={kind} goodWhenUp={goodWhenUp} />
      </td>
    </tr>
  );
}

// =====================================================================
// Per-metric, per-period no-data gating + detail (PR #19 sourceCounts)
// =====================================================================
// The problem these solve: a single store can have RECENT sales (October)
// but a backfilled, differently-dated expense feed (September). Both periods
// then hold *some* records, so a whole-statement "no data" check passes —
// yet metric-by-metric the coverage is lopsided, and a naive delta against an
// empty side reads as nonsense ("+$118,573", "-100%"). So each metric decides
// "has data" from its OWN underlying record counts, per period, and only shows
// a delta when BOTH sides actually recorded that metric.

// A calm, period-named note that replaces a delta when a side lacks the
// records behind a metric. `noun` is the thing that was not recorded, e.g.
// "September sales" — phrased so the sentence reads as a plain fact.
export function noDataPhrase(
  aHasData: boolean,
  bHasData: boolean,
  noun: string,
  aRange?: DateRange,
  bRange?: DateRange,
): string {
  const aName = aRange ? rangeLabel(aRange) : 'this period';
  const bName = bRange ? rangeLabel(bRange) : 'the prior period';
  // The missing side is the one whose noun we name — it is what's absent.
  if (!aHasData && !bHasData) return `No ${noun} recorded in either period — nothing to compare`;
  if (!bHasData) return `No ${noun} recorded for ${bName} — nothing to compare`;
  return `No ${noun} recorded for ${aName} — nothing to compare`;
}

export function NoDeltaNote({ text }: { text: string }) {
  return <span className="text-sm text-slate-500 italic">{text}</span>;
}

// A line of composition detail beneath a compared metric: the same label on
// each side with the record COUNT that justifies the figure (e.g. "8 sales
// days" / "0 sales days"), so a $41 or a $0 is explained rather than mysterious.
export interface DetailLine {
  label: string;
  a: string; // already-formatted, e.g. "8 days" or "3 invoices · $4,120.00"
  b: string;
  manual?: boolean; // flag a manual (hand-entered) line
}

// One metric compared across the two periods, gated on real records. Always
// shows each period's figure plainly; shows a Delta only when both sides have
// data for THIS metric, otherwise a calm note. Expands to composition detail
// (record counts) when `detail` is provided.
export function GatedCompareRow({
  label, a, b, kind, goodWhenUp, aHasData, bHasData, noun, aRange, bRange, detail, strong,
}: {
  label: string;
  a: number | null | undefined;
  b: number | null | undefined;
  kind: 'cents' | 'points';
  goodWhenUp: boolean;
  aHasData: boolean;
  bHasData: boolean;
  noun: string; // what "no data" means for this metric, e.g. "sales"
  aRange?: DateRange;
  bRange?: DateRange;
  detail?: DetailLine[];
  strong?: boolean;
}) {
  const [open, setOpen] = useState(false);
  const fmt = (v: number | null | undefined) =>
    v === null || v === undefined ? '—' : kind === 'cents' ? moneyCents(v) : `${v.toFixed(1)}%`;
  const bothHaveData = aHasData && bHasData;
  const hasDetail = !!detail && detail.length > 0;

  return (
    <>
      <tr className={clsx(hasDetail && 'cursor-pointer hover:bg-slate-50')} onClick={hasDetail ? () => setOpen((o) => !o) : undefined}>
        <td className={clsx('table-cell text-slate-700', strong ? 'font-semibold' : 'font-medium')}>
          <span className="inline-flex items-center gap-1">
            {hasDetail && (open ? <ChevronDown className="w-3.5 h-3.5 text-slate-400" /> : <ChevronRight className="w-3.5 h-3.5 text-slate-400" />)}
            {label}
          </span>
        </td>
        <td className={clsx('table-cell text-right tabular-nums', !aHasData && 'text-slate-400')}>{fmt(a)}</td>
        <td className={clsx('table-cell text-right tabular-nums', aHasData ? 'text-slate-500' : 'text-slate-400')}>{fmt(b)}</td>
        <td className="table-cell text-right">
          {bothHaveData ? (
            <Delta now={a} prev={b} kind={kind} goodWhenUp={goodWhenUp} />
          ) : (
            <NoDeltaNote text={noDataPhrase(aHasData, bHasData, noun, aRange, bRange)} />
          )}
        </td>
      </tr>
      {hasDetail && open && detail!.map((d, i) => (
        <tr key={`${label}-detail-${i}`} className="bg-slate-50/60 text-xs">
          <td className="table-cell pl-7 text-slate-500">
            ↳ {d.label}{d.manual && <span className="ml-1.5 badge bg-slate-200 text-slate-600">manual</span>}
          </td>
          <td className="table-cell text-right tabular-nums text-slate-500">{d.a}</td>
          <td className="table-cell text-right tabular-nums text-slate-400">{d.b}</td>
          <td className="table-cell" />
        </tr>
      ))}
    </>
  );
}

// The honest coverage-mismatch banner. When one period recorded sales but
// almost no expenses (or the reverse) the comparison is directional at best —
// say so once, at the top, derived purely from the record-count asymmetry
// (never hardcoded to a month). Returns null when coverage is even enough.
//
// `sales`/`expense` are the two record-count families for each period; a side
// "has" a family when its count is above `floor` (a single stray backfilled
// invoice should not count as real expense coverage). A mismatch is one side
// having sales-but-not-expenses while the comparison as a whole has both.
export function CoverageBanner({
  a, b, aRange, bRange, salesNoun = 'sales', expenseNoun = 'expenses', floor = 1,
}: {
  a: { sales: number; expense: number };
  b: { sales: number; expense: number };
  aRange: DateRange;
  bRange: DateRange;
  salesNoun?: string;
  expenseNoun?: string;
  floor?: number;
}) {
  const aSales = a.sales > floor;
  const aExp = a.expense > floor;
  const bSales = b.sales > floor;
  const bExp = b.expense > floor;

  // Describe only the lopsided sides. A side is lopsided when it has one
  // family of records but effectively lacks the other.
  const parts: string[] = [];
  const describe = (label: string, hasSales: boolean, hasExp: boolean) => {
    if (hasSales && !hasExp) parts.push(`${label} has ${salesNoun} but little ${expenseNoun} data`);
    else if (hasExp && !hasSales) parts.push(`${label} has ${expenseNoun} but little ${salesNoun} data`);
  };
  describe(rangeLabel(aRange), aSales, aExp);
  describe(rangeLabel(bRange), bSales, bExp);

  if (parts.length === 0) return null;

  return (
    <div className="px-4 pt-4">
      <div className="rounded-lg border border-warning-200 bg-warning-50 p-3 flex items-start gap-2">
        <Info className="w-4 h-4 text-warning-600 mt-0.5 shrink-0" />
        <p className="text-sm text-warning-800">
          These periods have uneven coverage — {parts.join('; ')}. Treat the comparison as directional.
        </p>
      </div>
    </div>
  );
}
